/**
 * fire_rules.ts — the PURE rules of the fire system (fire realism R1–R3,
 * 2026-09-25). No three, no DOM: `verify:fire` pins every number here.
 *
 *   · THE FLICKER. Measured flame luminance has a flat low-frequency spectrum
 *     rolling off at ≈1/f² — a one-pole low-pass of white noise — and a
 *     product of sines is PERIODIC, which the eye reads as an LED. `Flicker`
 *     is two one-pole low-passes of seeded white noise (fc ≈ 8 Hz and a
 *     slower 2.5 Hz layer) run at a FIXED internal rate, so the signal is the
 *     same at 30 or 144 fps. ONE flicker per fire drives the flame's exposure
 *     (±12 %), its height (±6 %) and its pool light (±20 % + ±4 cm of position
 *     jitter): flame and light pulse TOGETHER, which is what sells it.
 *   · THE POPS. A seeded Poisson stream per fire (0.3–1.2 /s): each pop fires
 *     the crackle transient, a burst of embers and an 80 ms light spike from
 *     the same seed, so ear and eye agree.
 *   · THE RUNGS. `fireQuality` (a tier flag) picks the fire shader family
 *     member: 'sprite' (minimal/potato), 'cards' (low, and every tier past
 *     40 m), 'volume' (med/high — the procedural box march, both backends),
 *     'sim' (ultra on WebGPU, the nearest 1–2 fires within 30 m). A per-fire
 *     hysteresis band keeps a fire standing at 40 m from swapping every frame.
 *   · THE LIGHT. A fire lights its surroundings visibly only when the ambient
 *     is low — the old 35 % daylight FLOOR is gone; the light scales with the
 *     night instead (a campfire at noon is invisible in the sun, as it is).
 *   · ON THE CURVE (session 3, 2026-09-26 — the lighting agent's physical-
 *     emissive contract, lib/photometry.ts). The shaders emit in their OWN
 *     units (the oracle's: the core ray ≈ 4.6 luminance at exposure 1 — what
 *     the bed renders); ONE scale per frame, `fireRadianceScale`, puts them in
 *     scene units: the core is a physical 10 kcd/m² (rule 1, `emissiveNits`)
 *     until the display runs out of room for its colour, then it is HELD on
 *     screen (rule 4, `emissiveEvScale`). The pool light is candela ×
 *     luxToScene (rule 2), cut by the same stops the flame is held under.
 */
import { emissiveNits, emissiveEvScale, keyLuminanceNits, type ExposureState } from '../photometry.js';

export type FireQuality = 'sprite' | 'cards' | 'volume' | 'sim';
export type FireRung = 'sprite' | 'cards' | 'volume' | 'sim';
export const FIRE_QUALITIES: readonly FireQuality[] = ['sprite', 'cards', 'volume', 'sim'];

// ── The rungs ────────────────────────────────────────────────────────────────
/** Past this a volume fire draws as cards (every tier). */
export const FIRE_CARDS_BEYOND_M = 40;
/** On WebGL 2 the march is capped nearer (weak iGPUs): cards past 20 m. */
export const FIRE_CARDS_BEYOND_M_WEBGL = 20;
/** The simulated rung: only the nearest fires, only this close. */
export const FIRE_SIM_WITHIN_M = 30;
export const FIRE_SIM_HEROES = 2;
/** Nothing past this draws (the town fixtures' old FIRE_DRAW_M). */
export const FIRE_DRAW_M = 180;
/** The rung-swap hysteresis half-band (m). */
export const FIRE_RUNG_HYSTERESIS_M = 3;
/** March steps per rung. */
export const FIRE_STEPS: Record<'med' | 'high' | 'sim', number> = { med: 12, high: 16, sim: 20 };

/**
 * The rung a fire draws at. `prev` = the rung it drew last frame (the
 * hysteresis: a fire already on the richer rung keeps it until it is
 * FIRE_RUNG_HYSTERESIS_M past the line; one on the cheaper rung needs to come
 * the same distance inside). `heroRank` = its rank by distance among the
 * fires in view (0 = nearest).
 */
export function fireRungFor(q: FireQuality, distM: number, webgpu: boolean, heroRank: number, prev: FireRung | null = null): FireRung {
  if (q === 'sprite') return 'sprite';
  if (q === 'cards') return 'cards';
  const h = FIRE_RUNG_HYSTERESIS_M;
  const cardsAt = webgpu ? FIRE_CARDS_BEYOND_M : FIRE_CARDS_BEYOND_M_WEBGL;
  const rich = prev === 'volume' || prev === 'sim';
  const volumeOk = distM <= cardsAt + (rich ? h : -h);
  if (!volumeOk) return 'cards';
  if (q === 'sim' && webgpu && heroRank < FIRE_SIM_HEROES) {
    const simOk = distM <= FIRE_SIM_WITHIN_M + (prev === 'sim' ? h : -h);
    if (simOk) return 'sim';
  }
  return 'volume';
}

/** The fire quality each tier ships with. */
export const FIRE_QUALITY_BY_TIER: Record<'minimal' | 'potato' | 'low' | 'med' | 'high' | 'ultra', FireQuality> = {
  minimal: 'sprite', potato: 'sprite', low: 'cards', med: 'volume', high: 'volume', ultra: 'volume' /* coordinator (2026-09-27): the SIM rung drew a glow with no flame body (fire CHECKPOINT 3). 2026-09-29: the gas rises now (fire_sim.ts THE FLOOR IS THE GROUND) but the plume clips at the box's walls — ultra keeps the volume flame, 'sim' stays selectable */,
};
/** Pool lights a tier may give to fires (fixed for the session — the light-set identity law). */
export const FIRE_LIGHTS_BY_TIER: Record<'minimal' | 'potato' | 'low' | 'med' | 'high' | 'ultra', number> = {
  minimal: 1, potato: 1, low: 2, med: 2, high: 3, ultra: 3,
};
/** The broker's whole pool: the fire share + 4 impact slots + 1 flash (fireworks / explosions). */
export function brokerCapacityFor(fireLights: number): number { return Math.max(1, Math.round(fireLights)) + 5; }

// ── The light ────────────────────────────────────────────────────────────────
/**
 * THE PRE-CURVE RULE (session 1), kept for the probe's A/B lever only
 * (`__fire().physical(false)` — the "before" picture): a fixed-unit pool light
 * of FIRE_LIGHT_FIXED_CD × this, 6 % at noon, full after dusk. Play never uses it.
 */
export function fireLightScale(night: number): number {
  const n = Math.min(1, Math.max(0, night));
  return 0.06 + 0.94 * n * n * (3 - 2 * n);
}
/** The pre-curve pool light in fixed scene units at size 1 (the A/B lever's "before"). */
export const FIRE_LIGHT_FIXED_CD = 18;

// ── On the curve (session 3, 2026-09-26) ─────────────────────────────────────
/**
 * The flame CORE's luminance (cd/m²). The blackbody LUT's top is 1,600 K,
 * whose luminance (Planck × V(λ)) is 20.9 kcd/m² — the lighting table's "hot
 * core of a flame" (`EMISSIVE_NITS.lampBulb`). It was HALF that until 2026-10-08
 * (a soot core at an emissivity of ≈ 0.5); see the note below. The flame's body
 * (the tongues, the edges) falls away from it through the LUT's T⁴, as the
 * shader already does.
 */
// 2026-10-08, the owner's call ("raise FIRE_CORE_NITS to 20000 so day fires stand out"): at 10,000 a noon flame was only
// 1.9× a sunlit 18 % card — a pale translucent orange in every day picture. 20,000 is the LUT top's own luminance (an
// emissivity near 1: the optically thick heart of a wood fire). NIGHT IS UNCHANGED BY CONSTRUCTION: the flame is HELD at
// its display ceiling there, and the two things defined FROM the core move with it — FIRE_LIGHT_CANDELA (derived below)
// and the fireball (0.8 of the core) — so the hold's extra stop cancels in the light and the fireball keeps its ratio.
export const FIRE_CORE_NITS = 20_000;
/**
 * The core ray's LUMINANCE in the shaders' own units at exposure 1 — the
 * oracle's `coreRayCpu` (a campfire's axis at 0.3 of its flame's height), the
 * mean over 8 seeds × 6 times and their puff phases (`verify:fire` re-marches
 * it and holds this to 3 %). 2026-10-08: re-measured for the rebuilt flame
 * (it was 4.63 for the cone of speckle, through the box at 0.25 of its height).
 */
export const FIRE_CORE_REF = 2.80;
/**
 * The brightest the core's MEAN may DISPLAY (display-linear luminance, the
 * renderer's exposure applied). Under AgX (the default operator) a 1,300–
 * 1,600 K orange keeps its hue — R − B ≥ 60 of 255 — only below ≈ 3, and
 * reads bright (L > 200) above ≈ 1.1: a 1.5-stop window. The core's mean sits
 * at 1.3 so its peak ray (× 1.35), the flicker (+ 12 %) and a pop (+ 15 %)
 * land inside it. The recipe's own cap (EMISSIVE_MAX_OVER_KEY_EV, stops over
 * the KEY) cannot do this: the key's display level falls 5.5 stops into the
 * night, so a flame 8–10 stops over it is orange at midnight and white at
 * dusk and blue hour (7–12 display-linear, computed on the curve).
 */
export const FIRE_CORE_DISPLAY_MAX = 1.3;
/**
 * The eye's local adaptation to firelight (stops). A display-held flame and
 * its light are ONE source, so the light is cut by the stops the flame is held
 * under — the firelit logs keep their physical ratio to the flame (≈ 4.8
 * stops under its core at 0.7 m) — less this lift: a human at a campfire sees
 * the ring and the faces round it plainly, ~2.5 stops under the flames,
 * which a global tone map cannot do by itself.
 */
export const FIRE_ADAPT_EV = 2.5;
/**
 * A campfire's luminous intensity (cd) at size 1: its flames' mean luminance
 * (≈ a third of the core) × their emitting area (≈ 0.45 m² of a 1.3 × 1.5 m
 * flame zone) ≈ 3,000 cd — grows with the fire's area.
 */
export const FIRE_LIGHT_CANDELA = (FIRE_CORE_NITS / 3) * 0.45; // 3,000 cd — DERIVED: a fixed 1,500 would halve the night's firelight when the core doubled (the light is cut by the stops the flame is held under)
export function fireCandela(size: number): number { return FIRE_LIGHT_CANDELA * size * size; }

/** The exposure a fire reads (lib/photometry's `exposureState` in play; the harness passes its own). */
export type FireExposure = Pick<ExposureState, 'evScene' | 'luxToScene' | 'exposure' | 'exposureRef'>;

/**
 * The flame core's SCENE luminance now: physical (rule 1 — `emissiveNits`: at
 * noon ≈ 3.8× an 18 % card in the sun, a warm flame that reads against sunlit ground, never clipped) until it would out-shine the
 * display's colour-holding white, then HELD there on screen (rule 4 —
 * `emissiveEvScale`: the metered correction cannot push it into white).
 */
export function fireCoreScene(s: FireExposure): number {
  // Rule 1 WITHOUT the key cap: on the curve the display hold below is always the lower of the two (the key cap sits
  // ≥ 4.8 scene units at midnight, the hold ≈ 1.5), and under the FIXED exposure mode (a real fixed camera: noon's
  // luxToScene, the night's key) the key cap would black a night fire out — a fixed camera shows its physical 0.4.
  const physical = emissiveNits(FIRE_CORE_NITS, Infinity, s);
  const held = (FIRE_CORE_DISPLAY_MAX / Math.max(1e-6, s.exposureRef)) * emissiveEvScale(0, s);
  return Math.min(physical, held);
}
/** The multiplier on every fire shader's own emission (flame, coals, embers, the smoke's under-glow): 1 = the bed's units. */
export function fireRadianceScale(s: FireExposure): number { return fireCoreScene(s) / FIRE_CORE_REF; }
/** How many stops the display holds the core under its physical luminance (0 by day). */
export function fireHeldStops(s: FireExposure): number {
  const physical = FIRE_CORE_NITS * Math.max(1e-12, s.luxToScene);
  return Math.max(0, Math.log2(physical / Math.max(1e-12, fireCoreScene(s))));
}
/**
 * A fire's pool light in SCENE units (a PointLight's intensity): rule 2,
 * candela × luxToScene — by day swamped by the sun (no floor, no scale by the
 * night: the physics does it) — cut by the stops the flame is held under less
 * FIRE_ADAPT_EV, so at night the pair is pinned on screen together.
 */
export function fireLightScene(size: number, s: FireExposure): number {
  const cut = Math.max(0, fireHeldStops(s) - FIRE_ADAPT_EV);
  return fireCandela(size) * Math.max(0, s.luxToScene) * Math.pow(2, -cut);
}
/** Its reach (m): unshadowed, so bounded — a leak through a thin wall stays local. */
export function fireLightRange(size: number): number { return Math.min(28, 10 + 8 * size); }
/** The flicker's share of each output. */
export const FLICKER_EXPOSURE = 0.12;
export const FLICKER_LIGHT = 0.2;
export const FLICKER_HEIGHT = 0.06;
export const FLICKER_JITTER_M = 0.04;
/** A pop's light spike. */
export const POP_SPIKE = 0.15;
export const POP_SPIKE_S = 0.08;

// ── The flicker filter ───────────────────────────────────────────────────────
/** A seeded uniform stream (mulberry32). */
export function rng32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The internal rate the filter integrates at (Hz): fixed, so the signal never depends on the frame rate. */
export const FLICKER_RATE_HZ = 240;
export const FLICKER_FC_HZ = 8;
export const FLICKER_FC2_HZ = 2.5;

/**
 * Two one-pole low-passes of unit-variance white noise, normalised to unit
 * variance each and mixed 0.8 : 0.6 (the 2.5 Hz layer is the slow breathing,
 * the 8 Hz one the lick), scaled so the output's σ ≈ 0.45 and clamped to
 * [−1, 1]. `step(dt)` advances wall time and returns the current value.
 */
export class Flicker {
  private readonly r: () => number;
  private readonly a1: number;
  private readonly a2: number;
  private readonly n1: number;
  private readonly n2: number;
  private y1 = 0;
  private y2 = 0;
  private acc = 0;
  value = 0;
  constructor(seed: number, fc = FLICKER_FC_HZ, fc2 = FLICKER_FC2_HZ) {
    this.r = rng32(seed);
    const h = 1 / FLICKER_RATE_HZ;
    this.a1 = 1 - Math.exp(-2 * Math.PI * fc * h);
    this.a2 = 1 - Math.exp(-2 * Math.PI * fc2 * h);
    // A one-pole y += a(x − y) driven by unit-variance noise has variance a / (2 − a).
    this.n1 = 1 / Math.sqrt(this.a1 / (2 - this.a1));
    this.n2 = 1 / Math.sqrt(this.a2 / (2 - this.a2));
    // Warm the filters so the first frame is already mid-signal, not a ramp from 0.
    for (let i = 0; i < 480; i++) this.tick();
  }
  private tick(): void {
    const w1 = (this.r() * 2 - 1) * Math.sqrt(3), w2 = (this.r() * 2 - 1) * Math.sqrt(3); // unit variance
    this.y1 += this.a1 * (w1 - this.y1);
    this.y2 += this.a2 * (w2 - this.y2);
    const s = (0.8 * this.y1 * this.n1 + 0.6 * this.y2 * this.n2) * 0.45;
    this.value = s < -1 ? -1 : s > 1 ? 1 : s;
  }
  step(dt: number): number {
    this.acc += Math.min(0.25, Math.max(0, dt));
    const h = 1 / FLICKER_RATE_HZ;
    while (this.acc >= h) { this.acc -= h; this.tick(); }
    return this.value;
  }
}

/** A seeded Poisson stream of events at `rateHz`. `step(dt)` returns how many fired in dt. */
export class PopStream {
  private readonly r: () => number;
  private next: number;
  constructor(seed: number, readonly rateHz: number) {
    this.r = rng32(seed ^ 0x9e3779b9);
    this.next = this.draw();
  }
  private draw(): number { return -Math.log(1 - this.r() * 0.999999) / Math.max(1e-6, this.rateHz); }
  step(dt: number): number {
    let n = 0;
    this.next -= Math.max(0, dt);
    while (this.next <= 0) { n++; this.next += this.draw(); }
    return n;
  }
  /** The seeded size of the next pop's ember burst (4–10). */
  burst(): number { return 4 + Math.floor(this.r() * 7); }
}
/** A fire's pop rate (Hz) from its seed and size: 0.3–1.2 /s, bigger fires pop more. */
export function popRateFor(seed: number, size: number): number {
  const r = rng32(seed ^ 0x51ed)();
  return Math.min(1.2, Math.max(0.3, 0.3 + 0.6 * r + 0.3 * Math.min(1, Math.max(0, size - 1))));
}

// ── Kinds ────────────────────────────────────────────────────────────────────
export type FireKind = 'campfire' | 'bonfire' | 'brazier' | 'torch' | 'hearth' | 'afterfire' | 'fireball';
/** Default size (1 = a campfire's 1.3 × 2.3 m box) and whether it smokes / throws embers / wants a char mark. */
export const FIRE_KIND: Record<FireKind, { size: number; smoke: boolean; embers: boolean; char: boolean; heat: number }> = {
  campfire: { size: 1, smoke: true, embers: true, char: true, heat: 1 },
  bonfire: { size: 1.8, smoke: true, embers: true, char: true, heat: 1.1 },
  brazier: { size: 0.55, smoke: true, embers: true, char: false, heat: 0.95 },
  torch: { size: 0.3, smoke: false, embers: false, char: false, heat: 0.9 },
  hearth: { size: 0.7, smoke: true, embers: true, char: false, heat: 0.95 },
  afterfire: { size: 0.9, smoke: true, embers: true, char: false, heat: 0.9 },
  fireball: { size: 2, smoke: false, embers: false, char: false, heat: 1.3 },
};

/** A fire's box (m) at size s: 1.3 wide (lean headroom), 2.3 tall. */
export function fireBox(size: number): { w: number; h: number } { return { w: 1.3 * size, h: 2.3 * size }; }

// ── A kind's own flame (fire realism, 2026-10-08) ────────────────────────────
/**
 * EVERY KIND WAS ONE FLAME, SCALED: a torch was a small bonfire. Measured on photographs by fire type
 * (docs/research/2026-10-08-fire-realism/references.md): a campfire is about as wide as it is tall (height ÷ width 1.0,
 * 0.9–1.5) with a broad base; a bonfire is taller (1.6) and thicker; a brazier's flame is ragged (the most broken
 * outline of all the types) over a contained bowl; a torch's flame is about as wide as it is tall (1.0), narrow at the
 * head, WIDENS upward and leans; a
 * burning wreck's flames are low and wide (0.7) under heavy smoke. The shader's envelope takes these per fire:
 *
 *   flameH  the envelope's height as a share of the box (the steady flame ends near 0.55 of it; tongues reach the top)
 *   baseR   the envelope's radius as a share of the box's width
 *   pinch   0 = a broad base (logs, a pile, a bowl) … 1 = a narrow root that widens upward (a torch head)
 *   tongues the tongue noise's lateral frequency (more = more, thinner tongues)
 *   rag     edge erosion (0 smooth … 1.2 ragged)
 *   sway    how far the flame's line wanders with height
 *   puff    the puffing bulge's amplitude (a share of the radius)
 *   soot    the in-flame soot plume's density (dark smoke at the tips: a wreck, a big pile)
 *   gain    emission against a campfire's (a torch is small — a thin flame — but its flame is the brightest of all)
 *   baseD   the fuel bed's diameter in metres at size 1 — the D of the puffing frequency
 */
export interface FireShape { flameH: number; baseR: number; pinch: number; tongues: number; rag: number; sway: number; puff: number; soot: number; gain: number; baseD: number }
export const FIRE_SHAPE: Record<FireKind, FireShape> = {
  campfire: { flameH: 0.60, baseR: 0.34, pinch: 0.30, tongues: 1.2, rag: 0.7, sway: 1.0, puff: 0.14, soot: 0.3, gain: 1.0, baseD: 0.8 },
  bonfire: { flameH: 0.78, baseR: 0.35, pinch: 0.23, tongues: 1.3, rag: 0.8, sway: 1.1, puff: 0.16, soot: 0.6, gain: 0.8, baseD: 0.85 },
  brazier: { flameH: 0.60, baseR: 0.34, pinch: 0.12, tongues: 1.45, rag: 1.3, sway: 1.2, puff: 0.18, soot: 0.25, gain: 1.5, baseD: 0.9 },
  torch: { flameH: 0.44, baseR: 0.36, pinch: 0.6, tongues: 0.95, rag: 0.8, sway: 1.5, puff: 0.22, soot: 0.15, gain: 2.6, baseD: 0.33 },
  hearth: { flameH: 0.50, baseR: 0.34, pinch: 0.12, tongues: 1.2, rag: 0.7, sway: 0.8, puff: 0.12, soot: 0.25, gain: 1.25, baseD: 0.9 },
  afterfire: { flameH: 0.42, baseR: 0.36, pinch: 0.08, tongues: 1.35, rag: 1.0, sway: 1.3, puff: 0.16, soot: 1.6, gain: 0.95, baseD: 1.1 },
  fireball: { flameH: 0.60, baseR: 0.34, pinch: 0.30, tongues: 1.2, rag: 0.7, sway: 1.0, puff: 0.14, soot: 0.3, gain: 1.0, baseD: 0.8 },
};
/** The flame's nominal height (m) for a kind at a size: its envelope's top over its base. */
export function fireFlameHeightM(kind: FireKind, size: number): number { return fireBox(size).h * FIRE_SHAPE[kind].flameH; }

// ── Motion (measured in the literature, not guessed) ────────────────────────
/**
 * THE PUFF. A buoyant diffusion flame sheds a vortex ring at f = 1.5 / √D Hz (D the fuel bed's diameter in metres —
 * Cetegen & Ahmed; f = 0.48·√(g/D), confirmed from 3 cm to 50 m pools): a 0.8 m campfire breathes at 1.7 Hz, a 1.5 m
 * bonfire at 1.2 Hz, a 0.1 m torch head at 4.7 Hz. The envelope's bulge and the pool light's swell run on this phase.
 */
export function firePuffHz(kind: FireKind, size: number): number { return 1.5 / Math.sqrt(Math.max(0.02, FIRE_SHAPE[kind].baseD * size)); }
/** The puff's period wanders with the fire's own flicker (a real fire's spectrum has a PEAK at f, not a line). */
export const PUFF_JITTER = 0.3;
/** How much of the puff reaches the pool light (the flame's whole light swells as the bulge fills the body). */
export const PUFF_LIGHT = 0.6;
/** How much of the drawn flame's own emission swing (fire_oracle `flameEmissionCpu` ÷ its running mean) reaches its light. */
export const LIGHT_FOLLOW = 0.5;
/** The running mean's time constant (s) and the ratio's clamp. */
export const LIGHT_FOLLOW_TAU_S = 1.5;
export const LIGHT_FOLLOW_RANGE: readonly [number, number] = [0.5, 1.6];
/**
 * THE FROUDE CLOCK. Buoyant speeds go as √(size) and distances as size, so a fire s times larger moves 1/√s as fast
 * measured in its own box: the per-fire shader clock runs at this rate (a bonfire rolls, a torch flutters).
 */
export function fireTimeScale(size: number): number { return 1 / Math.sqrt(Math.max(0.05, size)); }
/**
 * The speed (m/s) a flame feature climbs at, `zM` metres over the base of a fire of `size` — the CPU statement of the
 * shader's rising coordinate (fire_oracle FIRE_SHADER: rise = √(z/size·… + y0)·K − t·W on the Froude clock):
 * u = (2W/K)·√(z + y0·size) = 3.2·√z, half of McCaffrey's centreline gas speed 6.84·√z (structures ride at about half).
 */
export function fireRiseSpeed(zM: number, size: number, K = 0.62, W = 1.0, y0 = 0.05): number { return ((2 * W) / K) * Math.sqrt(Math.max(0, zM) + y0 * size); }

// ── Embers (R3) ──────────────────────────────────────────────────────────────
/** The ember ramp in LINEAR HDR (bloom picks the young ones up). */
export const EMBER_RAMP: ReadonlyArray<readonly [number, number, number]> = [[8, 6, 3], [4, 1.6, 0.3], [1.2, 0.25, 0.05], [0, 0, 0]];
export const EMBER_LIFE_S: readonly [number, number] = [1.5, 3];
/**
 * An ember's size (m) and its streak (fire realism, 2026-10-08): the first embers were 2–5 cm discs stretched ×3 — fat
 * ovals 10 cm long. Measured on photographs a spark is ≈ 0.013 of the flame's height across: a centimetre.
 */
export const EMBER_SIZE_M: readonly [number, number] = [0.009, 0.022];
export const EMBER_STRETCH = 2.2;
/** Embers a second from a campfire at full particle budget (the pops add their bursts). */
export const EMBER_RATE = 4.5;
/** Smoke puffs a second from a campfire's tips (the six-way column: small, thin puffs that overlap into one column). */
export const SMOKE_RATE = 8;
export const EMBER_GRAVITY = -0.15;
export const EMBER_DRAG = 0.8;
/** Embers per fire cluster at a particle cap (0..1). */
export function emberBudget(particleCap: number): number { return Math.round(Math.max(0, Math.min(1, particleCap)) * 120); }

// ── Fireball (R5): radius and temperature through its life ──────────────────
/** A ground fireball's radius (× its scale) and hotness 0..1 at age t of life L: expands fast, cools into smoke. */
export function fireballAt(t: number, life: number): { radius: number; heat: number; soot: number } {
  const u = Math.min(1, Math.max(0, t / Math.max(1e-3, life)));
  const radius = 0.35 + 0.65 * (1 - (1 - u) ** 3);
  const heat = Math.max(0, 1 - u * 1.25) ** 1.4;
  const soot = Math.min(1, u * 1.6);
  return { radius, heat, soot };
}

// ── §4.3 THE TRANSIENT EMITTERS ON THE CURVE (surfaces agent, 2026-09-27; research §4.3) ─────────────────────
/**
 * The fire domain's last fixed-unit emitters — the explosion's flash, the fireball, the fireworks' stars, the thruster
 * plume and the lightning channel — take the flame's recipe (lighting CHECKPOINT 3 rules 1 / 2 / 4, the fire's DISPLAY
 * HOLD): what an emitter SHOWS is its luminance in cd/m² × luxToScene (`emissiveNits`, physical — a firework by day is
 * a faint star, as it is) until that would out-shine its display ceiling, then it is HELD there (`emissiveEvScale`: the
 * metered correction cannot push it white); what it LIGHTS is candela × luxToScene, cut by the stops its own look is
 * held under less the eye's firelight adaptation (FIRE_ADAPT_EV) — one source, the look and the light pinned together.
 */
export const EMITTER_NITS = {
  /** An explosion's flash: the fireball's first 80 ms (a detonation's incandescent front). */
  blast: 50_000,
  /** The fireball's mean core: 0.8 of the campfire core (20,000) — the same held display, a hotter LUT stop. It moves WITH
   *  the core (2026-10-08): the shader scales it as fireball / FIRE_CORE_NITS, so a fixed 8,000 would halve it at night. */
  fireball: 16_000,
  /** A firework star (a pyrotechnic composition's burning pellet, as the eye sees it at a distance). */
  spark: 5_000,
  /** A thruster plume's body (the hovercraft's exhaust). */
  plume: 600,
  /** A lightning return stroke's channel. */
  lightning: 1_000_000,
} as const;
/**
 * The display-linear ceilings each emitter is HELD at (the fire core's 1.3 is FIRE_CORE_DISPLAY_MAX): a blast may near-
 * white (3); a coloured star keeps its hue under AgX below ≈ 1.5 (1.2 — the pre-curve night look, ≈ 1 scene × 0.85); the
 * plume sits with the flame (1.3); the lightning channel 3 stops over the key's display (0.18 × 2³ = 1.44), the brief's
 * "held ≤ 3 stops over the key".
 */
export const EMITTER_DISPLAY_MAX = { blast: 3.0, spark: 1.2, plume: 1.3, lightning: 0.18 * 8 } as const;
/** The blast flash's duration (ms): the incandescent front, not the fireball's life. */
export const BLAST_FLASH_MS = 80;
/** A blast's flash as a light: the fireball's disc at 80 ms (radius ≈ 1 m per unit of scale) at the flash's luminance. */
export function blastFlashCandela(scale: number): number { return EMITTER_NITS.blast * Math.PI * scale * scale; }
/** A firework break's light at scale 1 (cd): ~a hundred stars at a few hundred cd each — 1.25 lux at 200 m. */
export const FIREWORK_BREAK_CANDELA = 50_000;
/** A return stroke's peak luminous intensity (cd): cloud-to-ground, and an in-cloud stroke seen through the deck. */
export const LIGHTNING_PEAK_CD = { cg: 2e8, ic: 6e7 } as const;
/** The lightning flash may light the land at most this many stops over the scene's key illuminance. */
export const LIGHTNING_LIGHT_MAX_OVER_KEY_EV = 3;

/** An emitter's scene luminance: physical `nits` (rule 1, no key cap) until held at `displayMax` display-linear (rule 4). */
export function emitterScene(nits: number, displayMax: number, s: FireExposure): number {
  const physical = emissiveNits(nits, Infinity, s);
  const held = (displayMax / Math.max(1e-6, s.exposureRef)) * emissiveEvScale(0, s);
  return Math.min(physical, held);
}
/** How many stops the display holds an emitter under its physical luminance (0 while physical). */
export function emitterHeldStops(nits: number, displayMax: number, s: FireExposure): number {
  const physical = nits * Math.max(1e-12, s.luxToScene);
  return Math.max(0, Math.log2(physical / Math.max(1e-12, emitterScene(nits, displayMax, s))));
}
/** An emitter's LIGHT (a PointLight's intensity): candela × luxToScene (rule 2), cut by its held stops less FIRE_ADAPT_EV. */
export function emitterLightScene(candela: number, nits: number, displayMax: number, s: FireExposure): number {
  const cut = Math.max(0, emitterHeldStops(nits, displayMax, s) - FIRE_ADAPT_EV);
  return Math.max(0, candela) * Math.max(0, s.luxToScene) * Math.pow(2, -cut);
}
/**
 * The lightning flash's illuminance at the viewer in scene units (a DirectionalLight's intensity): the stroke's peak
 * candela / d², capped LIGHTNING_LIGHT_MAX_OVER_KEY_EV stops over the key's own illuminance (an 18 % card's luminance × π
 * / 0.18) — invisible under a noon sun (200 lux vs 100 klx), a white flash over a moonlit field.
 */
export function lightningLightScene(kind: 'cg' | 'ic', distM: number, s: FireExposure): number {
  const lux = LIGHTNING_PEAK_CD[kind] / Math.max(1, distM * distM);
  const keyLux = keyLuminanceNits(s) * Math.PI / 0.18;
  return Math.min(lux, keyLux * Math.pow(2, LIGHTNING_LIGHT_MAX_OVER_KEY_EV)) * Math.max(0, s.luxToScene);
}

/**
 * §4.4 THE FIRE HAZE (surfaces, 2026-09-27; Halladay's screen-space distortion over a rising noise column): the
 * nearest FIRE_HAZE_MAX fires within FIRE_HAZE_M carry a refraction quad (each is a mid-pass scene copy's consumer and
 * a transparent overdraw layer — a festival of 20 braziers paid 20 before). Its strength follows the fire's HEAT
 * OUTPUT (its size), NOT the exposure: refraction by hot air is geometry, not light — a radiance-scaled haze would
 * vanish by day, when shimmer over a fire is plainest against a bright background.
 */
export const FIRE_HAZE_MAX = 3;
export const FIRE_HAZE_M = 28;
export function fireHazeK(size: number): number { return Math.min(2, Math.max(0.5, size)); }

/** Distance (m) → the delay of an explosion's report (the speed of sound). */
export const SPEED_OF_SOUND = 343;
export function soundDelayS(distM: number): number { return Math.max(0, distM) / SPEED_OF_SOUND; }

// ── Thruster plumes (R9): Mach diamonds ─────────────────────────────────────
/**
 * Shock diamonds sit ≈ 1.2–1.5 nozzle radii apart in an under-expanded jet
 * (the Prandtl estimate x ≈ 1.22·D·√(M²−1) for a sonic-ish exit collapses to
 * a period of order the nozzle DIAMETER; the brief's 1.2–1.5 radii reads
 * right at game scale). Brightness per cell: a raised cosine to the 8th power
 * (sharp knots), decaying along the axis as the jet mixes out.
 */
export const MACH_SPACING_R = 1.35;
export const MACH_SHARPNESS = 8;
export const MACH_DECAY_CELLS = 4;
/** The diamonds' brightness at `dM` metres from the nozzle mouth (0..1), for a nozzle of radius `nozzleR` at `throttle`. */
export function machDiamonds(dM: number, nozzleR: number, throttle: number, spacingR = MACH_SPACING_R): number {
  const phase = dM / Math.max(1e-4, spacingR * nozzleR);
  const cell = (0.5 + 0.5 * Math.cos(2 * Math.PI * phase)) ** MACH_SHARPNESS;
  const t = Math.min(1, Math.max(0, throttle));
  return cell * Math.exp(-phase / MACH_DECAY_CELLS) * t * t;
}
